import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { NotificationPanel } from "../components/sharedComponents/notifications/NotificationPanel";
import {
  fetchUserNotifications,
  markAllUserNotificationsRead,
  markNotificationRecipientRead,
} from "../api/notificationApi";
import GetLocalStorageUserId from "../components/sharedComponents/GetLocalStorageuserId";
import { usesSignalRNotifications } from "../utils/jwtRole";
import { mapApiNotificationsToView } from "../utils/notificationMappers";
import { NOTIFICATIONS_REFRESH_EVENT } from "../types/notification.types";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  const refreshNotifications = useCallback(async () => {
    const userId = GetLocalStorageUserId();
    if (!userId || !usesSignalRNotifications()) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    setFetchError(null);

    try {
      const items = await fetchUserNotifications(userId, {
        unreadOnly: false,
        page: 1,
        pageSize: 20,
      });
      setNotifications(mapApiNotificationsToView(items));
    } catch (err) {
      console.warn("[Notifications] Failed to load", err);
      setFetchError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!usesSignalRNotifications()) {
      setNotifications([]);
      return undefined;
    }

    refreshNotifications();

    const onAuthUpdate = () => {
      if (usesSignalRNotifications()) refreshNotifications();
      else setNotifications([]);
    };

    const onRefresh = () => refreshNotifications();

    window.addEventListener("sundorbon-auth-updated", onAuthUpdate);
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);

    return () => {
      window.removeEventListener("sundorbon-auth-updated", onAuthUpdate);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    };
  }, [refreshNotifications]);

  const markAsRead = useCallback(
    async (recipientId) => {
      const userId = GetLocalStorageUserId();
      if (!userId || recipientId == null) return;

      try {
        await markNotificationRecipientRead(recipientId, userId);
        setNotifications((prev) =>
          prev.map((n) =>
            n.recipientId === recipientId ? { ...n, read: true } : n,
          ),
        );
      } catch (err) {
        console.warn("[Notifications] Mark read failed", err);
        await refreshNotifications();
      }
    },
    [refreshNotifications],
  );

  const markAllAsRead = useCallback(async () => {
    const userId = GetLocalStorageUserId();
    if (!userId) return;

    try {
      await markAllUserNotificationsRead(userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.warn("[Notifications] Mark all read failed", err);
      await refreshNotifications();
    }
  }, [refreshNotifications]);

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const togglePanel = useCallback(() => setPanelOpen((v) => !v), []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      panelOpen,
      loading,
      fetchError,
      refreshNotifications,
      markAsRead,
      markAllAsRead,
      openPanel,
      closePanel,
      togglePanel,
    }),
    [
      notifications,
      unreadCount,
      panelOpen,
      loading,
      fetchError,
      refreshNotifications,
      markAsRead,
      markAllAsRead,
      openPanel,
      closePanel,
      togglePanel,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationPanel />
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}

export default NotificationProvider;
